import { useMemo, useState } from "react";
import { Archive, RotateCcw } from "lucide-react";
import { Button, EmptyState, Spinner, toast } from "@/ui";
import { useEngineMutation, useEngineQuery } from "@/engine/hooks";
import { useOpenContact } from "@/app/navigation";
import { Page } from "@/app/layout/Page";
import { SearchBox } from "@/features/people/SearchBox";
import { ContactRowView } from "@/features/people/ContactRowView";
import { FilterPanel, useGroupNames } from "@/features/people/FilterPanel";
import { ChipRow, useQueryChips } from "@/features/people/ChipRow";
import { clearPart, composeQuery, emptyFilter, filterParts, type PeopleFilter } from "@/features/people/filter";
import s from "../shared.module.css";

export function ArchivePage() {
  const [query, setQuery] = useState("");
  // Archived is fixed on here, as on the phone's Archive screen; the panel hides the row.
  const [panel, setPanel] = useState<PeopleFilter>(() => ({ ...emptyFilter("last_interaction"), archived: true }));
  const filter = useMemo(() => ({ ...panel, archived: true }), [panel]);
  const groupNames = useGroupNames();
  const parts = useMemo(() => filterParts(filter, groupNames), [filter, groupNames]);
  const composed = composeQuery(parts.map((p) => p.clause).join(" "), query);
  const open = useOpenContact();
  const rows = useEngineQuery("searchArchivedContacts", { query: composed || undefined, limit: 500 }, { placeholderData: (p) => p });
  const restore = useEngineMutation("restoreContact", { invalidate: ["searchArchivedContacts", "queryContacts", "getContact", "getContactCount"] });
  const chips = useQueryChips({ parts: parts.filter((p) => p.key !== "archived"), typed: query, onClearPart: (key) => setPanel((f) => clearPart(f, key)), onReset: () => setPanel({ ...emptyFilter("last_interaction"), archived: true }), onTyped: setQuery });
  const total = rows.data?.total ?? 0;
  return (
    <Page flush title="Archive" actions={<span className="u-small u-muted">{rows.data ? `${total} archived` : ""}</span>}>
      <ChipRow chips={chips.chips} onRemove={chips.remove} onClear={chips.clear} />
      <div className={s.toolbar}>
        <SearchBox value={query} onChange={setQuery} showHints={false} showParsed={false} placeholder="Search archived people" />
        <FilterPanel filter={filter} onChange={(f) => setPanel({ ...f, archived: true })} count={total} archivedFixed sorts={[]} />
      </div>
      <div style={{ flex: 1, overflowY: "auto" }}>
        {rows.isLoading && <div style={{ padding: 24, display: "flex", justifyContent: "center" }}><Spinner /></div>}
        {rows.isSuccess && rows.data.items.length === 0 && <EmptyState icon={<Archive size={26} className="u-muted" />} title="Nothing archived">Archived people are hidden from lists and suggestions but keep all their history. Restore them any time.</EmptyState>}
        {rows.data?.items.map((r) => (
          <div key={r.contactId} className="u-row" style={{ gap: 0 }}>
            <div className="u-grow"><ContactRowView row={r} showCheckbox={false} onOpen={() => open(r.contactId, { page: true })} /></div>
            <Button size="sm" variant="outline" icon={<RotateCcw size={13} />} style={{ marginRight: 12 }} onClick={() => restore.mutate({ contactId: r.contactId }, { onSuccess: () => toast(`Restored ${r.displayName}`) })}>Restore</Button>
          </div>
        ))}
      </div>
    </Page>
  );
}
